import { QuestionCircleOutlined } from '@ant-design/icons'
import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil
} from '@cannect/assets/img'
import { useAuth } from '@cannect/hooks/AuthContext'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { SoldByPillText } from '@cannect/new-components/atoms'
import { formatToReal } from '@cannect/utils/number'
import { Tooltip } from 'antd'
import { Trash2 } from 'lucide-react'
import { FlavorQuantitySelector } from 'new-components/FlavorQuantitySelector'
import { ProgressiveDiscountBadge } from 'new-components/ProgressiveDiscountBadge'
import { ProgressiveSingleQuantityControl, useProgressiveUnitPrice } from 'new-components/ProgressiveQuantityDropdown'

interface CartItemProps {
  item: any
  onRemove: (productId: number) => void
  onQuantityChange: (productId: number, quantity: number) => void
  index: number
  isLoading?: boolean
}

function CartItem({ item, onRemove, onQuantityChange, index: _index, isLoading = false }: CartItemProps) {
  const { patientIsUser: _patientIsUser, coupon } = usePrepareCart()
  const { role } = useAuth()

  const productId: number = item?.product?.id || 0
  const basePrice = parseFloat(item?.unitPrice || '0') || item?.product?.real_price || item?.product?.price || 0
  const effectiveUnitPrice = useProgressiveUnitPrice(productId, item?.quantity || 1, basePrice)

  const getProductImage = (product: any) => {
    const isNationalProduct = product?.product?.supplier?.id === 18
    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const imgMedia = product?.product?.media?.find((m: any) => m?.format === 'IMG')?.path
    const shapeNameFormatted = product?.product?.shape_id
    const isPillProduct = product?.product?.origin === 'pill'
    const isSupplementProduct = product?.product?.class_id === 6

    const isPatientUserType = role === 1

    let imageSource = ''

    if (isPillProduct) {
      imageSource = imgMedia;
      return imageSource;
    }

    switch (shapeNameFormatted) {
      case 1:
        imageSource =
          isPatientUserType && !isSupplementProduct ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        break
      case 2:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCapsule
            : imgMedia || cannectDefaultProductCapsule
        break
      case 4:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCream
            : imgMedia || cannectDefaultProductCream
        break
      case 6:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductGummy
            : imgMedia || cannectDefaultProductGummy
        break
      default:
        imageSource = isPatientUserType && !isSupplementProduct ? defaultImage : imgMedia || defaultImage
        break
    }

    return imageSource
  }

  const originalPrice = parseFloat(item?.promotion?.regular_price || '0')
  const promotionalPrice = parseFloat(item?.promotion?.promotional_price || '0')
  const hasPromotion = promotionalPrice > 0 && promotionalPrice < originalPrice
  // Progressivo só vence a promoção quando de fato reduziu o preço (faixa atingida sobre basePrice).
  const isProgressiveActive = effectiveUnitPrice < basePrice
  const progressiveDiscountPct = isProgressiveActive
    ? Math.round(((basePrice - effectiveUnitPrice) / basePrice) * 100)
    : 0

  // Item coberto por cupom (discount_type !== 'progressive'): o backend já resolveu o preço da
  // linha — usa item.amount/item.discount reais em vez de recalcular via promotion/progressivo,
  // que não sabem nada sobre cupom.
  const isCouponDiscounted = !!coupon && !!item?.discount_type && item.discount_type !== 'progressive'
  const couponQty = Number(item?.quantity) || 1
  const couponAmount = parseFloat(item?.amount || '0')
  const couponDiscountTotal = parseFloat(item?.discount || '0')
  let couponUnitPrice = basePrice
  if (couponAmount > 0) {
    couponUnitPrice = couponAmount / couponQty
  } else if (couponDiscountTotal > 0) {
    couponUnitPrice = Math.max(0, basePrice - couponDiscountTotal / couponQty)
  }

  let finalPrice = effectiveUnitPrice
  if (isCouponDiscounted) {
    finalPrice = couponUnitPrice
  } else if (isProgressiveActive) {
    finalPrice = effectiveUnitPrice
  } else if (hasPromotion) {
    finalPrice = promotionalPrice
  }

  // Percentual do cupom derivado do antes/depois real (basePrice x couponUnitPrice) — sempre
  // consistente com o preço final exibido, independente de qual campo (amount ou discount)
  // definiu couponUnitPrice acima.
  const couponHasDiscount = isCouponDiscounted && couponUnitPrice < basePrice
  const couponDiscountPct =
    couponHasDiscount && basePrice > 0 ? Math.round(((basePrice - couponUnitPrice) / basePrice) * 100) : 0

  let discount = 0
  if (hasPromotion) {
    discount = item?.promotion?.discount_value
  }

  const isEditable = item.type === 'PRODUTO' || item.type === 'TESTE_GENETICO'

  const isUnavailable = (item?.sellable ?? item?.product?.sellable ?? true) === false

  return (
    <div
      className={`sm:gap- relative flex items-start gap-3 rounded-lg border ${isUnavailable ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-white'} p-3 sm:p-4 transition-opacity${isLoading ? 'pointer-events-none opacity-60' : ''}`}>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
          <svg className="h-5 w-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>
      )}
      <img
        src={getProductImage(item)}
        alt={item.product?.translated_name || item.product?.name}
        className="mt-8 h-[80px] w-[80px] flex-shrink-0 rounded-lg object-cover sm:h-[105px] sm:w-[125px]"
      />

      <div className="flex flex-1 flex-col gap-2">
        {isEditable && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                if (productId) onRemove(productId)
              }}
              className="flex items-center gap-1 rounded-md border-none bg-transparent p-1 text-[#E87883] outline-none hover:text-[#D35D6A] focus:outline-none focus:ring-2 focus:ring-[#6B7B3C] focus:ring-offset-1">
              <Trash2 className="h-4 w-4" />
              <span className="text-xs font-medium">Remover</span>
            </button>
          </div>
        )}

        {/* Badge de desconto progressivo acima do nome */}
        <ProgressiveDiscountBadge productId={productId} />

        <p className="text-xs font-medium text-gray-800 sm:text-sm">
          {item.product?.translated_name || item.product?.name}{' '}
          {item.product?.info && (
            <Tooltip title={item.product?.info}>
              <QuestionCircleOutlined style={{ color: '#6B7B3C', marginLeft: '4px' }} />
            </Tooltip>
          )}
        </p>

        {isUnavailable && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-red-600">Este produto está indisponível no momento.</p>
            <button
              onClick={() => {
                if (productId) onRemove(productId)
              }}
              className="flex shrink-0 items-center gap-1 rounded-md border-none bg-transparent p-1 text-[#E87883] outline-none hover:text-[#D35D6A] focus:outline-none">
              <Trash2 className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Remover</span>
            </button>
          </div>
        )}

        {item.product?.origin === 'pill' && (
          <div className="flex flex-col">
            <SoldByPillText />
          </div>
        )}

        {item.type === 'PRODUTO' && (
          <FlavorQuantitySelector
            productId={productId}
            mainProductId={item?.product?.main_product_id}
            currentFlavorLabel={item.product?.flavor}
            overlay
            className="text-xs text-gray-600 sm:text-sm"
          />
        )}

        <div className="mt-2 flex flex-row items-center justify-between gap-2">
          {isEditable ? (
            <ProgressiveSingleQuantityControl
              productId={productId}
              qty={item.quantity || 1}
              basePrice={basePrice}
              stock={item?.product?.stock}
              overlay
              onSelect={(n) => onQuantityChange(productId, n)}
            />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center text-sm font-medium text-gray-600">
              {item.quantity}
            </span>
          )}

          <div className="flex flex-col items-end">
            {couponHasDiscount && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400 line-through">{formatToReal(basePrice)}</span>
                <span className="font-medium text-green-600">-{couponDiscountPct}%</span>
              </div>
            )}
            {!isCouponDiscounted && isProgressiveActive && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400 line-through">{formatToReal(basePrice)}</span>
                <span className="font-medium text-green-600">-{progressiveDiscountPct}%</span>
              </div>
            )}
            {!isCouponDiscounted && !isProgressiveActive && hasPromotion && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400 line-through">{formatToReal(originalPrice)}</span>
                <span className="font-medium text-green-600">-{parseFloat(discount.toString())}%</span>
              </div>
            )}
            <span className="text-sm font-semibold text-gray-900">{formatToReal(finalPrice)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CartItemsProps {
  items: any[]
  onRemove: (productId: number) => void
  onQuantityChange: (productId: number, quantity: number) => void
  /** Total real de itens no carrinho (inclui agrupados). Usado para evitar empty state falso. */
  totalCartItems?: number
  /** IDs de produtos com request em andamento — exibe spinner na linha e bloqueia interação. */
  loadingProductIds?: Set<number>
}

export function CartItems({ items, onRemove, onQuantityChange, totalCartItems, loadingProductIds }: CartItemsProps) {
  if (!items.length && !(totalCartItems && totalCartItems > 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 h-16 w-16 text-gray-300">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">Seu carrinho está vazio</h3>
        <p className="text-sm text-gray-500">Adicione produtos para começar suas compras</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const pid: number = item?.product?.id || 0
        return (
          <CartItem
            key={item.id || index}
            item={item}
            onRemove={onRemove}
            onQuantityChange={onQuantityChange}
            index={index}
            isLoading={loadingProductIds?.has(pid) ?? false}
          />
        )
      })}
    </div>
  )
}
